import toNumber from 'lodash/toNumber'
import {
  path,
  set,
  lensPath,
  pick,
  pipe,
  defaultTo,
  mergeDeepLeft,
  dissocPath,
  reject,
  propEq
} from 'ramda'
import { batch } from 'react-redux'
import { getSession, deleteFlag, createFlag } from '@/ports'
import { pushToDatalayer } from '@/utils/push-datalayer'

const setGA4UserData = session =>
  pushToDatalayer({
    event: 'setUserData',
    pageType: 'lakshmi',
    userId: session.id,
    userType: session.user_type,
    userEmail: session.username,
    userFirstName: session.first_name,
    userLastName: session.last_name,
    userPhone: session.phone_number
  })

const setGA4NonLoggedUserData = () =>
  window.dataLayer.push({
    event: 'setUserData',
    pageType: 'lakshmi',
    userId: undefined,
    userType: 'nonlogged',
    userEmail: undefined,
    userFirstName: undefined,
    userLastName: undefined,
    userPhone: undefined
  })

const selectData = pick([
  'agreements',
  'session',
  'users',
  'companies',
  'company_users',
  'user_secure_files',
  'user_social_media_tokens',
  'user_flags',
  'beta_users'
])

class SessionError extends Error {
  constructor(data) {
    super()
    this.data = data
  }
}

const getNestedObjectKeys = (obj, pathToNestedObj) => pipe(path(pathToNestedObj), Object.keys)(obj)

const patch = (original, at, payload) =>
  set(lensPath(at), pipe(path(at), defaultTo({}), mergeDeepLeft(payload))(original), original)

const session = {
  state: null,
  reducers: {
    update: (state, at, payload) => set(lensPath(at), payload, state),
    remove: (state, at) => dissocPath(at, state),
    patch,
    patchExisting: (state, at, payload) =>
      patch(state, at, pick(getNestedObjectKeys(state, at), payload)),
    set: (state, payload) => (state ? payload : { ...state, ...payload }),
    updateContract: (state, payload) =>
      state ? { ...state, contracts: { ...state.contracts, ...payload } } : null,
    updateSession: (state, payload) =>
      state ? { ...state, session: { ...state.session, ...payload } } : null,
    clearData: () => null
  },
  effects: dispatch => ({
    clear(payload = {}) {
      batch(() => {
        if (!payload.keepSession) {
          dispatch.session.clearData()
        }
        dispatch.profile.clear()
        dispatch.application.clear()
        dispatch.chat.clear()
        dispatch.supportRoom.clear()
        dispatch.contract.clear()
        dispatch.listing.clear()
        dispatch.contract.clearSteps()
        dispatch.toaster.clear()
        dispatch.companyManagement.clear()
        dispatch.settlements.clear()
      })
      localStorage.removeItem('promoType')
      setGA4NonLoggedUserData()
    },
    async loadSession(opts) {
      const { response, body = {} } = await getSession()
      if (!response.ok) {
        throw new SessionError(body)
      }
      if (body.session) {
        localStorage.setItem('session-id', body.session.id)
        setGA4UserData(body.session)
        if (opts?.eventId) {
          pushToDatalayer({
            event: `${opts.eventId}-${body.session.operation_type}`
          })
        }
        batch(() => {
          dispatch.session.set(selectData(body))
          dispatch.profile.update(['profile_progress'], body.session?.profile_progress?.break_down)
          if (body.session.registration_complete === '1') {
            dispatch.profile.load()
            dispatch.toaster.load(body.session?.user_type)
          }
        })
      } else {
        setGA4NonLoggedUserData()
      }
    },
    setIdVerificationState(idState) {
      dispatch.session.update(['session', 'identity_verified_state'], idState)
      dispatch.profile.update(['identity_verified_state'], idState)
    },
    deleteUserFlag: async (flagId, rootState) => {
      try {
        const { response, body } = await deleteFlag(
          {
            body: {
              id: toNumber(flagId)
            }
          },
          { flagId }
        )
        if (response.ok) {
          const updatedFlags = reject(propEq('id', flagId), rootState.session.user_flags)
          dispatch.session.update(['user_flags'], updatedFlags)
          return response
        }
        throw new Error(body.message || response.statusText)
      } catch (error) {
        console.log('[session/deleteUserFlag]', error)
      }
    },
    createFlag: async (flag, rootState) => {
      try {
        const { response, body } = await createFlag({
          body: {
            key_name: flag.name,
            key_value: flag.value
          }
        })
        if (response.ok) {
          dispatch.session.patch(['user_flags'], body)
          return response
        }
        throw new Error(body.message || response.statusText)
      } catch (error) {
        console.log('[session/createUserFlag]', error)
      }
    }
  }),
  selectors: {
    registrationPlan() {
      return path(['profile', 'company_info', 'registration_plan'])
    },
    accountType() {
      return path(['profile', 'company_info', 'account_type'])
    }
  }
}

export default session
